import { useEffect, useRef } from "react";
import anime from "animejs";

type Status = "uploading" | "extracting" | "extracted" | "failed" | "error";

interface StatusIconProps {
  status: Status;
}

const ICON_SIZE = 20;

export function StatusIcon({ status }: StatusIconProps) {
  switch (status) {
    case "uploading":
      return <UploadingIcon />;
    case "extracting":
      return <ExtractingIcon />;
    case "extracted":
      return <ExtractedIcon />;
    case "failed":
    case "error":
      return <FailedIcon />;
    default:
      return null;
  }
}

/** Arrow pulsing up/down */
function UploadingIcon() {
  const arrowRef = useRef<SVGGElement>(null);

  useEffect(() => {
    const el = arrowRef.current;
    if (!el) return;

    const anim = anime({
      targets: el,
      translateY: [-2, 2],
      easing: "easeInOutSine",
      duration: 1200,
      direction: "alternate",
      loop: true,
    });

    return () => anim.pause();
  }, []);

  return (
    <svg
      width={ICON_SIZE}
      height={ICON_SIZE}
      viewBox="0 0 20 20"
      fill="none"
      className="shrink-0"
    >
      <g ref={arrowRef}>
        <path
          d="M10 14V4"
          stroke="var(--tr-text-2)"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M6 8l4-4 4 4"
          stroke="var(--tr-text-2)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

/** Spinning ring */
function ExtractingIcon() {
  const ringRef = useRef<SVGCircleElement>(null);
  const containerRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const anim = anime({
      targets: container,
      rotate: 360,
      easing: "linear",
      duration: 1500,
      loop: true,
    });

    return () => anim.pause();
  }, []);

  const radius = 7;
  const circumference = 2 * Math.PI * radius;

  return (
    <svg
      ref={containerRef}
      width={ICON_SIZE}
      height={ICON_SIZE}
      viewBox="0 0 20 20"
      fill="none"
      className="shrink-0"
      style={{ willChange: "transform" }}
    >
      <circle
        cx="10"
        cy="10"
        r={radius}
        stroke="var(--tr-border-3)"
        strokeWidth="1.5"
        fill="none"
      />
      <circle
        ref={ringRef}
        cx="10"
        cy="10"
        r={radius}
        stroke="var(--tr-info)"
        strokeWidth="1.5"
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * 0.7}
        strokeLinecap="round"
        transform="rotate(-90 10 10)"
      />
    </svg>
  );
}

/** Checkmark that draws itself in */
function ExtractedIcon() {
  const checkRef = useRef<SVGPathElement>(null);
  const containerRef = useRef<SVGSVGElement>(null);
  const animated = useRef(false);

  useEffect(() => {
    const check = checkRef.current;
    const container = containerRef.current;
    if (!check || !container || animated.current) return;
    animated.current = true;

    const length = check.getTotalLength();
    check.style.strokeDasharray = `${length}`;
    check.style.strokeDashoffset = `${length}`;

    // Draw checkmark
    anime({
      targets: check,
      strokeDashoffset: [length, 0],
      easing: "easeOutQuad",
      duration: 400,
      delay: 100,
    });

    // Scale bounce
    anime({
      targets: container,
      scale: [0.6, 1.15, 1],
      easing: "easeOutBack",
      duration: 500,
    });
  }, []);

  return (
    <svg
      ref={containerRef}
      width={ICON_SIZE}
      height={ICON_SIZE}
      viewBox="0 0 20 20"
      fill="none"
      className="shrink-0"
      style={{ willChange: "transform" }}
    >
      <circle cx="10" cy="10" r="8" stroke="var(--tr-ok)" strokeWidth="1.5" fill="none" />
      <path
        ref={checkRef}
        d="M6.5 10.5l2.5 2.5 5-5"
        stroke="var(--tr-ok)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/** X icon with shake */
function FailedIcon() {
  const containerRef = useRef<SVGSVGElement>(null);
  const animated = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || animated.current) return;
    animated.current = true;

    anime({
      targets: container,
      translateX: [-4, 4, -3, 3, -1, 0],
      easing: "easeOutQuad",
      duration: 400,
    });
  }, []);

  return (
    <svg
      ref={containerRef}
      width={ICON_SIZE}
      height={ICON_SIZE}
      viewBox="0 0 20 20"
      fill="none"
      className="shrink-0"
      style={{ willChange: "transform" }}
    >
      <circle cx="10" cy="10" r="8" stroke="var(--tr-bad)" strokeWidth="1.5" fill="none" />
      <path
        d="M7.5 7.5l5 5M12.5 7.5l-5 5"
        stroke="var(--tr-bad)"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
